import type { Request, RequestAttachment } from '@/lib/types/request.types';

/**
 * Normalizes request attachments from backend shape (url, type, size, …) to frontend shape (filePath, fileType, fileSize).
 * Use this whenever you receive request.attachments from the API without going through transformBackendRequest
 * (e.g. when using extractResponseData only).
 */
export function normalizeRequestAttachments(attachments: any[] | null | undefined): RequestAttachment[] {
  if (!Array.isArray(attachments)) return [];
  return attachments.map((att: any) => {
    const camelAtt = typeof att === 'object' && att !== null ? toCamelCase(att) : {};
    const url = camelAtt.url ?? camelAtt.filePath ?? '';
    const fileType = camelAtt.fileType ?? camelAtt.type ?? '';
    const fileSize = camelAtt.fileSize ?? camelAtt.size ?? 0;
    const uploadedAt = camelAtt.uploadedAt ?? camelAtt.uploaded_at ?? '';
    return {
      id: camelAtt.id ?? '',
      filePath: url,
      fileType,
      fileSize,
      uploadedAt,
    };
  });
}

/**
 * Converts a snake_case string to camelCase
 */
function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

/**
 * Converts an object with snake_case keys to camelCase keys
 */
export function toCamelCase<T extends Record<string, any>>(obj: T): any {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => toCamelCase(item));
  }

  if (typeof obj !== 'object') {
    return obj;
  }

  const result: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    const camelKey = snakeToCamel(key);

    // Handle nested objects and arrays
    if (value !== null && typeof value === 'object') {
      if (Array.isArray(value)) {
        result[camelKey] = value.map((item) =>
          typeof item === 'object' && item !== null ? toCamelCase(item) : item
        );
      } else {
        result[camelKey] = toCamelCase(value);
      }
    } else {
      result[camelKey] = value;
    }
  }

  return result;
}

/**
 * Maps backend request status to frontend request status
 * Backend uses: 'published', 'in_bidding', 'draft', 'closed', 'cancelled', 'completed'
 * Frontend uses: 'open', 'in_progress', 'published', 'draft', 'closed', 'cancelled', 'completed'
 */
function mapBackendStatusToFrontend(backendStatus: string): string {
  const statusMap: Record<string, string> = {
    published: 'open', // 'published' in backend = 'open' in frontend (available for interaction)
    in_bidding: 'in_progress', // 'in_bidding' in backend = 'in_progress' in frontend
    draft: 'draft',
    closed: 'closed',
    cancelled: 'cancelled',
    completed: 'completed',
  };

  return statusMap[backendStatus] || backendStatus; // Fallback to original if not mapped
}

/**
 * Transforms backend request data (snake_case) to frontend Request type (camelCase)
 */
export function transformBackendRequest(backendRequest: any): Request {
  const transformed = toCamelCase(backendRequest);

  // Map backend status to frontend status
  const frontendStatus = mapBackendStatusToFrontend(transformed.status || 'draft');

  const officialBooklet = transformed.officialBooklet ?? transformed.official_booklet ?? null;
  const aiEnhancedDescription =
    transformed.aiEnhancedDescription ?? transformed.ai_enhanced_description;
  const aiProjectPhases = transformed.aiProjectPhases ?? transformed.ai_project_phases;
  const aiBillOfQuantities =
    transformed.aiBillOfQuantities ?? transformed.ai_bill_of_quantities ?? [];
  const aiPaymentSchedule = transformed.aiPaymentSchedule ?? transformed.ai_payment_schedule ?? [];
  const aiSuggestedTimeline = transformed.aiSuggestedTimeline ?? transformed.ai_suggested_timeline;
  const aiAppliedFields = transformed.aiAppliedFields ?? transformed.ai_applied_fields;

  const client = transformed.client
    ? {
        id: transformed.client.id || '',
        fullName: transformed.client.fullName || transformed.client.full_name || '',
        email: transformed.client.email || '',
      }
    : undefined;

  return {
    id: transformed.id || '',
    requestNumber: transformed.requestNumber || '',
    clientId: transformed.clientId || '',
    client,
    title: transformed.title || '',
    description: transformed.description || '',
    categoryId: transformed.categoryId || '',
    category: transformed.category ? toCamelCase(transformed.category) : undefined,
    budgetMin: transformed.budgetMin ?? null,
    budgetMax: transformed.budgetMax ?? null,
    expectedDuration: transformed.expectedDuration || 0,
    deadline: transformed.deadline || '',
    preferredStartDate: transformed.preferredStartDate ?? null,
    attachments: normalizeRequestAttachments(transformed.attachments),
    status: frontendStatus as any,
    selectedOfferId: transformed.selectedOfferId ?? null,
    viewsCount: transformed.viewsCount || 0,
    offersCount: transformed.offersCount || 0,
    requiresOnSiteVisit: transformed.requiresOnSiteVisit || false,
    location: transformed.location ? toCamelCase(transformed.location) : null,

    // AI fields
    ai_enhanced_description: aiEnhancedDescription,
    ai_project_phases: aiProjectPhases,
    ai_bill_of_quantities: aiBillOfQuantities,
    ai_payment_schedule: aiPaymentSchedule,
    ai_suggested_timeline: aiSuggestedTimeline,
    ai_applied_fields: aiAppliedFields,

    official_booklet: officialBooklet ? toCamelCase(officialBooklet) : null,

    createdAt: transformed.createdAt || '',
    updatedAt: transformed.updatedAt || '',
    publishedAt: transformed.publishedAt ?? null,
    closedAt: transformed.closedAt ?? null,
  };
}
